/**
 * EdgeResizeHandle — the modern side-panel resize affordance: an invisible
 * full-height strip along one edge of a resizable panel whose thin pill
 * fades in only on hover, keyboard focus, or during an active drag.
 *
 * Extracted from SlideoutMenu's private handle so every drag-resizable
 * panel (slideouts, the report editor's config rail) shares a single
 * idiom instead of hand-copied variants that drift apart.
 *
 * The handle sits fully INSIDE the panel (`w-2` strip flush against the
 * given edge; `h-2` for the bottom/top edges) — position it inside a
 * `relative` panel container. If that container scrolls, wrap it: the
 * handle must be a sibling of the scrolling box, not a child, or it scrolls
 * away with the content.
 */

import { GripCorner } from '@/lib/base/foundations/grip-corner';
import { cx } from '@/utils/cx';

/** Pixels per arrow-key press. */
const KEYBOARD_STEP = 20;

interface EdgeResizeHandleProps {
  /**
   * Which edge of the panel the handle sits on. A `left` handle belongs to
   * a right-anchored panel (slideout); a `right` handle to a left-anchored
   * panel (config rail); a `bottom` handle belongs to a top-anchored panel
   * and resizes height instead of width; a `top` handle belongs to a
   * bottom-anchored panel (e.g. the report drill panel) and also resizes
   * height, with growing/shrinking keyboard semantics mirrored relative to
   * `bottom` (dragging/pressing toward the panel's anchored edge shrinks it).
   */
  edge: 'left' | 'right' | 'bottom' | 'top';
  onResizeStart: (e: React.MouseEvent) => void;
  /** Called with a signed delta: positive grows the panel. */
  onKeyboardResize: (delta: number) => void;
  /** For a `bottom` handle, this is a height in px. */
  width: number;
  /** For a `bottom` handle, this is a min height in px. */
  minWidth: number;
  /** For a `bottom` handle, this is a max height in px. */
  maxWidth: number;
  /** Keeps the pill visible during an active drag. */
  isResizing?: boolean;
  'aria-label': string;
  onDoubleClick?: (e: React.MouseEvent) => void;
  /** Keyboard-only reset path (e.g. to auto-height); double-click is mouse-only. */
  onReset?: () => void;
  /**
   * Bottom edge only: render a small always-visible grip in the bottom-right
   * corner so the affordance is discoverable without hovering.
   */
  cornerGrip?: boolean;
}

export function EdgeResizeHandle({
  edge,
  onResizeStart,
  onKeyboardResize,
  width,
  minWidth,
  maxWidth,
  isResizing = false,
  'aria-label': ariaLabel,
  onDoubleClick,
  onReset,
  cornerGrip = false,
}: EdgeResizeHandleProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (edge === 'bottom' || edge === 'top') {
      // A `bottom` handle grows on ArrowDown (the panel is anchored at the
      // top, so moving its bottom edge down grows it). A `top` handle grows
      // on ArrowUp instead (the panel is anchored at the bottom, so moving
      // its top edge up grows it) — the mirror image.
      const growKey = edge === 'bottom' ? 'ArrowDown' : 'ArrowUp';
      const shrinkKey = edge === 'bottom' ? 'ArrowUp' : 'ArrowDown';
      if (e.key === growKey) {
        e.preventDefault();
        onKeyboardResize(KEYBOARD_STEP);
      } else if (e.key === shrinkKey) {
        e.preventDefault();
        onKeyboardResize(-KEYBOARD_STEP);
      } else if (e.key === 'Home') {
        e.preventDefault();
        onReset?.();
      }
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      onReset?.();
      return;
    }

    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      return;
    }
    e.preventDefault();
    // Spatial semantics: the arrow moves the handle in that direction, so
    // pressing the arrow that points AWAY from the panel grows it. A left
    // handle (right-anchored panel) grows on ArrowLeft; a right handle
    // (left-anchored panel) grows on ArrowRight.
    const grows = (e.key === 'ArrowLeft') === (edge === 'left');
    onKeyboardResize(grows ? KEYBOARD_STEP : -KEYBOARD_STEP);
  };

  return (
    <div
      className={cx(
        edge === 'bottom' || edge === 'top'
          ? 'group absolute right-0 left-0 z-10 flex h-2 cursor-row-resize items-center justify-center focus:outline-none'
          : 'group absolute top-0 bottom-0 z-10 flex w-2 cursor-col-resize items-center justify-center focus:outline-none',
        edge === 'left' && 'left-0',
        edge === 'right' && 'right-0',
        edge === 'bottom' && 'bottom-0',
        edge === 'top' && 'top-0'
      )}
      onMouseDown={onResizeStart}
      onDoubleClick={onDoubleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role='separator'
      aria-orientation={
        edge === 'bottom' || edge === 'top' ? 'horizontal' : 'vertical'
      }
      aria-label={ariaLabel}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
    >
      <div
        className={cx(
          'rounded-full transition-colors duration-200',
          edge === 'bottom' || edge === 'top' ? 'h-0.5 w-8' : 'h-8 w-0.5',
          isResizing
            ? 'bg-[var(--color-border-secondary)]'
            : 'bg-transparent group-hover:bg-[var(--color-border-secondary)]/70 group-focus-visible:bg-[var(--color-border-secondary)]/70'
        )}
      />
      {edge === 'bottom' && cornerGrip && (
        <GripCorner
          className={cx(
            'absolute right-1 bottom-0.5 size-2.5 transition-colors duration-200',
            isResizing
              ? 'text-fg-tertiary'
              : 'text-fg-quaternary group-hover:text-fg-tertiary group-focus-visible:text-fg-tertiary'
          )}
        />
      )}
    </div>
  );
}
